/**
 * Entitlement aggregation + display — the SINGLE semantics authority shared by the
 * signing freeze (apps/api services/entitlements.ts delegates here), the offer
 * builders' live "Included entitlements" panels, the offer page/PDF render, and the
 * public catalog feed the website prices from. Keeping the aggregation here (one
 * function object) makes display-vs-freeze drift impossible by construction.
 *
 * Vocabulary: the DB `entitlement_kind` table (migration 0032) is the runtime
 * authority — kinds have a value_type driving how limit_value is read:
 *   limit      → decimal string, NULL = unlimited
 *   capability → 'true' / 'false' (an explicit false is a signed exclusion)
 *   tier       → a token from the kind's ordered allowed_values (low→high)
 * Display labels live HERE (not in DB columns) because persisted strings stay
 * canonical English and the customer app overlays German at render time.
 */
import { VOLUME_TIERS, VOLUME_TIER_ORDER } from "./live-volume";

export type EntitlementValueType = "limit" | "capability" | "tier";

/** A vocabulary row as carried on the public catalog feed / admin catalog view. */
export interface EntitlementKindMeta {
  kind: string;
  value_type: EntitlementValueType;
  unit: string | null; // display unit: tickets | requests | seats | currency | ...
  default_period: "month" | "year" | "none";
  allowed_values: string[]; // ordered low→high for value_type='tier'; [] otherwise
  description?: string | null;
  sort_order: number;
}

/** One catalog_limit row resolved to its plan — the aggregation input. */
export interface PlanEntitlementRow {
  family: string;
  plan_key: string;
  limit_kind: string;
  limit_value: string | null;
  period: string;
  currency: string | null;
  volume_index: number | null; // catalog row's band (Live event_capacity); NULL = whole plan
  overage_policy: string;
  overage_rate_json: string | null;
}

/** The slice of an offer line the aggregation needs. */
export interface EntitlementLineRef {
  family: string;
  plan_key: string;
  volume_index: number | null;
  quantity?: number;
}

/** An aggregated entitlement — the exact shape the signing freeze stores. */
export interface AggregatedEntitlement {
  limit_kind: string;
  limit_value: string | null;
  period: string;
  currency: string | null;
  overage_policy: string;
  value_type: string; // limit | capability | tier
  overage_rate_json: string | null;
  source_family: string;
  source_plan_key: string;
  volume_index: number | null; // the LINE's volume_index (freeze provenance)
}

/** Per-kind merge metadata: how to interpret + rank values. */
export interface EntitlementKindMergeMeta {
  value_type: string;
  order: string[]; // ordered low→high enum for value_type='tier'; [] otherwise
}
export type KindMetaMap = Map<string, EntitlementKindMergeMeta>;

/** Build the merge-metadata map from vocabulary rows (feed/store consumers). */
export function kindMetaMap(kinds: EntitlementKindMeta[]): KindMetaMap {
  return new Map(kinds.map((k) => [k.kind, { value_type: k.value_type, order: k.allowed_values ?? [] }]));
}

const valueTypeOf = (kindMeta: KindMetaMap, kind: string): string => kindMeta.get(kind)?.value_type ?? "limit";

/** Concrete event_capacity for a Live line: explicit cap if set, else the volume tier. */
export function eventCapacityValue(limit: { limit_value: string | null }, lineVolumeIndex: number | null): string | null {
  if (limit.limit_value !== null) return limit.limit_value; // Essentials 25000, Scout 250k/1.5M/5M
  const vi = lineVolumeIndex ?? 0;
  const tier = VOLUME_TIERS[vi] ?? VOLUME_TIERS[vi < 0 ? 0 : VOLUME_TIER_ORDER.at(-1)!];
  return tier != null ? String(tier) : null;
}

/**
 * Does `cand` beat `prev` for a kind of the given value_type? The "most generous wins"
 * rule generalised across value types:
 *   • limit      → unlimited (NULL) beats any number; otherwise the larger number.
 *   • capability → 'true' beats 'false' (any grant across lines wins over an exclusion).
 *   • tier       → the higher-ranked token in the kind's ordered allowed_values wins.
 */
export function entitlementCandWins(
  cand: { limit_value: string | null },
  prev: { limit_value: string | null },
  meta: EntitlementKindMergeMeta | undefined,
): boolean {
  const vt = meta?.value_type ?? "limit";
  if (vt === "capability") return cand.limit_value === "true" && prev.limit_value !== "true";
  if (vt === "tier") {
    const order = meta?.order ?? [];
    return order.indexOf(cand.limit_value ?? "") > order.indexOf(prev.limit_value ?? "");
  }
  // limit
  if (prev.limit_value === null) return false; // already unlimited
  return cand.limit_value === null || Number(cand.limit_value) > Number(prev.limit_value);
}

/**
 * Resolve the entitlements a set of offer lines implies against per-plan catalog limits,
 * aggregating by (kind, currency) — most generous wins per value type (entitlementCandWins).
 * Pure; the signing freeze, the resolver fallback, and every display surface share it.
 */
export function aggregateEntitlements(
  lines: EntitlementLineRef[],
  limitsByPlan: Map<string, PlanEntitlementRow[]>, // key `${family}|${plan_key}`
  kindMeta: KindMetaMap,
): AggregatedEntitlement[] {
  const byKey = new Map<string, AggregatedEntitlement>();
  const additiveByKey = new Map<string, AggregatedEntitlement>();
  const additivePlans = new Set([
    "prisma_core_additional_user",
    "prisma_coreplus_additional_user",
  ]);
  for (const line of lines) {
    const planLimits = limitsByPlan.get(`${line.family}|${line.plan_key}`) ?? [];
    // A plan can define a whole-plan default (volume_index NULL) and override it for
    // individual Live capacity tiers. Resolve each (kind, currency) to the exact band
    // when present; otherwise inherit the plan default. Rows for OTHER bands must not
    // leak into this line and win merely because their numeric allowance is larger.
    const applicable = new Map<string, PlanEntitlementRow>();
    for (const lim of planLimits) {
      if (lim.volume_index !== null && lim.volume_index !== line.volume_index) continue;
      const key = `${lim.limit_kind}|${lim.currency ?? ""}`;
      const prev = applicable.get(key);
      if (!prev || (lim.volume_index !== null && prev.volume_index === null)) applicable.set(key, lim);
    }
    for (const lim of applicable.values()) {
      const value = lim.limit_kind === "event_capacity" ? eventCapacityValue(lim, line.volume_index) : lim.limit_value;
      const key = `${lim.limit_kind}|${lim.currency ?? ""}`;
      const cand: AggregatedEntitlement = {
        limit_kind: lim.limit_kind,
        limit_value: value,
        period: lim.period,
        currency: lim.currency,
        overage_policy: lim.overage_policy,
        value_type: valueTypeOf(kindMeta, lim.limit_kind),
        overage_rate_json: lim.overage_rate_json,
        source_family: line.family,
        source_plan_key: line.plan_key,
        volume_index: line.volume_index,
      };
      if (
        additivePlans.has(line.plan_key) &&
        (lim.limit_kind === "seats" || lim.limit_kind === "credits")
      ) {
        const previous = additiveByKey.get(key);
        const quantity = Math.max(1, Math.floor(line.quantity ?? 1));
        const addedValue = cand.limit_value === null ? null : String(Number(cand.limit_value) * quantity);
        if (!previous) {
          additiveByKey.set(key, { ...cand, limit_value: addedValue });
        } else {
          additiveByKey.set(key, {
            ...cand,
            limit_value:
              previous.limit_value === null || addedValue === null
                ? null
                : String(Number(previous.limit_value) + Number(addedValue)),
          });
        }
        continue;
      }
      const prev = byKey.get(key);
      if (!prev || entitlementCandWins(cand, prev, kindMeta.get(lim.limit_kind))) byKey.set(key, cand);
    }
  }
  for (const [key, addon] of additiveByKey) {
    const base = byKey.get(key);
    if (!base) {
      byKey.set(key, addon);
      continue;
    }
    byKey.set(key, {
      ...addon,
      limit_value:
        base.limit_value === null || addon.limit_value === null
          ? null
          : String(Number(base.limit_value) + Number(addon.limit_value)),
    });
  }
  return [...byKey.values()];
}

// ── Display formatting (canonical English; the customer app overlays German) ──

/** Kind → display label. Unknown kinds fall back to a title-cased slug. */
export const ENTITLEMENT_KIND_LABELS: Record<string, string> = {
  event_capacity: "Annual ticket capacity",
  managed_ad_spend: "Managed ad spend",
  api_calls: "API requests",
  seats: "User seats",
  credits: "Credits",
  cocreate_images: "CoCreate images", // legacy offer snapshots only; API vocabulary is credits
  data_integration_sources: "Data Integration sources",
  forecasts: "Forecasts",
  scenario_configs: "Scenario configurations",
  refinement_rounds: "Refinement rounds",
  scenarios: "Scenarios",
  promoter_split: "Promoter Split",
  "app.lookout": "Lookout",
  "app.wave": "Wave",
  "app.backhaul": "Backhaul",
  "app.sentinel": "Sentinel",
  live_location: "Live Location targeting",
  meta_ads: "Meta ads platform",
  google_display: "Google Display channel",
  sentinel_ai: "Sentinel AI querying",
  auto_sales_imports: "Automated sales imports",
  data_integration: "Data Integration",
  support_tier: "Support",
  sla: "Service-level agreement",
  account_manager: "Account manager",
  onboarding_tier: "Onboarding",
  business_review_cadence: "Business reviews",
};

/** Tier kind → token → display label. Unknown tokens render as the raw token. */
export const TIER_VALUE_LABELS: Record<string, Record<string, string>> = {
  support_tier: {
    form: "Form-based support",
    standard: "Standard support",
    phone_email: "Phone & email support",
    priority: "Priority support",
  },
  sla: { none: "No SLA", standard: "Standard SLA" },
  account_manager: { none: "None", shared: "Shared", dedicated: "Dedicated" },
  onboarding_tier: { self_serve: "Self-serve", guided: "Guided", concierge: "Concierge" },
  business_review_cadence: { none: "None", quarterly: "Quarterly", monthly: "Monthly" },
};

export function entitlementKindLabel(kind: string): string {
  const mapped = ENTITLEMENT_KIND_LABELS[kind];
  if (mapped) return mapped;
  const slug = kind.replace(/^app\./, "").replace(/[_.]/g, " ");
  return slug.charAt(0).toUpperCase() + slug.slice(1);
}

const CCY_SYMBOL: Record<string, string> = { EUR: "€", USD: "$" };
const PERIOD_SUFFIX: Record<string, string> = { month: " / month", year: " / year", none: "" };

/**
 * Human value for one entitlement: "500,000 tickets / year", "Unlimited",
 * "€10,000 / month", "Included" / "Not included", "Phone & email support".
 */
export function formatEntitlementValue(
  e: { limit_kind: string; value_type: string; limit_value: string | null; period: string; currency: string | null },
  meta?: EntitlementKindMeta | null,
): string {
  if (e.value_type === "capability") return e.limit_value === "true" ? "Included" : "Not included";
  if (e.value_type === "tier") return TIER_VALUE_LABELS[e.limit_kind]?.[e.limit_value ?? ""] ?? (e.limit_value || "—");
  // limit
  if (e.limit_value === null) return "Unlimited";
  const n = Number(e.limit_value);
  const num = Number.isFinite(n) ? n.toLocaleString("en-US") : e.limit_value;
  const suffix = PERIOD_SUFFIX[e.period] ?? "";
  if (meta?.unit === "currency") return `${CCY_SYMBOL[e.currency ?? "EUR"] ?? ""}${num}${suffix}`;
  const unit = meta?.unit ? ` ${meta.unit}` : "";
  return `${num}${unit}${suffix}`;
}

/** One formatted row for an entitlements panel/section. */
export interface EntitlementDisplayItem {
  kind: string;
  label: string;
  value: string;
  raw: AggregatedEntitlement;
}
export interface GroupedEntitlements {
  limits: EntitlementDisplayItem[];
  capabilities: EntitlementDisplayItem[];
  tiers: EntitlementDisplayItem[];
}

/**
 * Group aggregated entitlements for display, ordered by the vocabulary's sort_order
 * (unknown kinds sink to the end, ordered by kind). Every surface — builders, offer
 * page, PDFs — renders from this so the grouping/order is identical everywhere.
 */
export function groupEntitlementsForDisplay(list: AggregatedEntitlement[], kinds: EntitlementKindMeta[]): GroupedEntitlements {
  const metaByKind = new Map(kinds.map((k) => [k.kind, k]));
  const sortKey = (e: AggregatedEntitlement): [number, string] => [metaByKind.get(e.limit_kind)?.sort_order ?? Number.MAX_SAFE_INTEGER, e.limit_kind];
  const item = (e: AggregatedEntitlement): EntitlementDisplayItem => ({
    kind: e.limit_kind,
    label: entitlementKindLabel(e.limit_kind),
    value: formatEntitlementValue(e, metaByKind.get(e.limit_kind) ?? null),
    raw: e,
  });
  const sorted = [...list].sort((a, b) => {
    const [oa, ka] = sortKey(a);
    const [ob, kb] = sortKey(b);
    return oa - ob || ka.localeCompare(kb) || (a.currency ?? "").localeCompare(b.currency ?? "");
  });
  const out: GroupedEntitlements = { limits: [], capabilities: [], tiers: [] };
  for (const e of sorted) {
    if (e.value_type === "capability") out.capabilities.push(item(e));
    else if (e.value_type === "tier") out.tiers.push(item(e));
    else out.limits.push(item(e));
  }
  return out;
}

// ── Offer-snapshot section (shared type: API embeds it, SPAs/PDF render it) ───

/** One phase window of an offer's embedded entitlements. start/end are cumulative
 *  month OFFSETS from the term start (real dates materialize at signing); a single
 *  whole-term window carries null/null/null. */
export interface EntitlementWindow {
  phase_index: number | null;
  start_months: number | null;
  end_months: number | null;
  entitlements: AggregatedEntitlement[];
}
/** The `entitlements` section a version-2 offer snapshot embeds (bound by
 *  content_hash) — what the offer page/PDFs render and the signing freeze stores. */
export interface OfferEntitlementsSection {
  schema: 1;
  catalog_version_id: string;
  windows: EntitlementWindow[];
}

// ── Client-side store (hydrated from /api/public/catalog, like setCatalog) ────

/** One plan's catalog entitlement rows as carried on the public feed. */
export type CatalogPlanEntitlement = Omit<PlanEntitlementRow, "family" | "plan_key"> & {
  source_label?: string | null;
};

/** The additive `entitlements` section of the public catalog feed. */
export interface CatalogEntitlements {
  kinds: EntitlementKindMeta[];
  plans: Array<{ family: string; plan_key: string; entitlements: CatalogPlanEntitlement[] }>;
}

// Unlike the catalog (which has a bundled fallback), there is NO build-time default for
// entitlements — consumers hide their panels until the live feed hydrates the store.
let activeEntitlements: CatalogEntitlements | null = null;
let activeLimitsByPlan: Map<string, PlanEntitlementRow[]> | null = null;
let activeKindMeta: KindMetaMap | null = null;

/** Hydrate (or clear, with null) the entitlements store from the public catalog feed. */
export function setCatalogEntitlements(next: CatalogEntitlements | null): void {
  activeEntitlements = next;
  if (!next) {
    activeLimitsByPlan = null;
    activeKindMeta = null;
    return;
  }
  const byPlan = new Map<string, PlanEntitlementRow[]>();
  for (const p of next.plans) {
    byPlan.set(
      `${p.family}|${p.plan_key}`,
      p.entitlements.map((e) => ({ ...e, family: p.family, plan_key: p.plan_key })),
    );
  }
  activeLimitsByPlan = byPlan;
  activeKindMeta = kindMetaMap(next.kinds);
}

export function getCatalogEntitlements(): CatalogEntitlements | null {
  return activeEntitlements;
}

/**
 * Aggregate the entitlements the given draft lines imply, against the hydrated store.
 * Returns null when the store is empty (feed not hydrated / older API) — callers hide
 * their entitlements panel rather than showing a misleading empty state.
 */
export function resolveDisplayEntitlements(lines: EntitlementLineRef[]): AggregatedEntitlement[] | null {
  if (!activeLimitsByPlan || !activeKindMeta) return null;
  return aggregateEntitlements(lines, activeLimitsByPlan, activeKindMeta);
}
